/* eslint-disable styled-components-a11y/anchor-is-valid, styled-components-a11y/click-events-have-key-events, styled-components-a11y/no-static-element-interactions */
import type { LinkProps } from 'next/link';
import Link from 'next/link';
import type { RefObject } from 'react';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { Language } from '@core/internationalize/models/language';
import { useMembersRouter, localizeUrl } from '@members/localization/useMembersRouter';

export type NavLinkProps = LinkProps & {
  activeClassName?: string;
  alias?: string;
  'aria-label'?: string;
  className?: string;
  children?: React.ReactNode;
  exact?: boolean;
  id?: string;
  innerRef?: RefObject<HTMLAnchorElement>;
  isActive?: (() => boolean) | boolean;
  isExternal?: boolean;
  lang?: Language;
  tabIndex?: number;
  target?: string;
  title?: string;
};

const NavLink = ({
  activeClassName,
  alias,
  'aria-label': ariaLabel,
  className,
  children,
  exact,
  href,
  id,
  innerRef,
  isActive,
  isExternal,
  lang,
  onClick,
  tabIndex,
  title,
  ...props
}: NavLinkProps) => {
  const { asPath, isReady } = useMembersRouter();
  const [computedClassName, setComputedClassName] = useState(className);

  // For locale-prefixed routes (e.g. /de-DE), Next.js's <Link> href must remain
  // the original route path ('/feed') so the router can find the page component.
  // Passing a locale-prefixed href ('/de-DE/feed') causes Next.js to look for a
  // non-existent route and fall back to a hard navigation that loses the locale.
  //
  // Instead we only localize the `as` prop (the browser display URL).
  // alias is used as the `as` base when provided; otherwise href is used.
  // localizeUrl is a no-op for en-US and when LOCALE_TOGGLE is disabled.
  //
  // When href is a query-only UrlObject (e.g. { query: { modal: 'foo' } }) with
  // no pathname, localizeUrl can't add the locale prefix because there's nothing
  // to prefix. Synthesize the display URL by combining the current pathname
  // (without locale) with the query so localizeUrl can properly localize it.
  let hrefForDisplay = alias !== undefined ? alias : href;
  if (
    !isExternal &&
    alias === undefined &&
    typeof href === 'object' &&
    !href.pathname &&
    href.query
  ) {
    // Ensure the synthesised pathname always has a trailing slash.
    // Without it, navigating from a URL like /de-DE/profile/overview (no slash)
    // produces a display URL without a slash, which Cloudflare Pages redirects
    // to the trailing-slash version — dropping any modal query params in the
    // process and causing a spurious page reload.
    const rawPath = asPath.split('?')[0];
    hrefForDisplay = {
      ...href,
      pathname: rawPath.endsWith('/') ? rawPath : `${rawPath}/`,
    };
  }
  const displayUrl = isExternal ? alias ?? href : localizeUrl(hrefForDisplay);

  useEffect(() => {
    // Check if the router fields are updated client-side
    if (isReady) {
      let isActiveClassName = false;

      if (typeof isActive === 'boolean') {
        isActiveClassName = isActive;
      } else if (exact) {
        // Compare asPath (without locale) to href (also without locale), not displayUrl
        const compareUrl = alias ?? href;
        isActiveClassName =
          asPath === compareUrl ||
          (typeof compareUrl === 'object' && asPath === compareUrl.pathname);
      } else {
        // Compare asPath (without locale) to href (also without locale), not displayUrl
        const compareUrl = alias ?? href;
        isActiveClassName =
          asPath.startsWith(String(compareUrl)) ||
          (typeof compareUrl === 'object' && asPath.startsWith(compareUrl.pathname!));
      }

      isActiveClassName
        ? setComputedClassName(`${className} ${activeClassName}`)
        : setComputedClassName(className);
    }
  }, [activeClassName, asPath, className, exact, href, alias, isActive, isReady]);

  return isExternal ? (
    <a
      target="_blank"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props} // Spread remaining LinkProps for external links
      aria-label={ariaLabel}
      href={href as string}
      className={computedClassName}
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ) : (
    <Link
      as={displayUrl}
      href={href}
      locale={false}
      id={id}
      scroll={false}
      passHref
      legacyBehavior
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props} // Spread remaining LinkProps (prefetch, replace, shallow, locale, etc.)
    >
      <StyledLink
        aria-label={ariaLabel}
        className={computedClassName}
        data-test-id={(props as any)['data-test-id']}
        ref={innerRef}
        lang={lang}
        onClick={onClick}
        tabIndex={tabIndex}
        title={title}
      >
        {children}
      </StyledLink>
    </Link>
  );
};

const StyledLink = styled.a``;

export default NavLink;
